import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { CANONICAL_ORIGIN } from "@/components/SEO";
import {
  ArrowRight,
  CheckCircle2,
  Wifi,
  Tv,
  Smartphone,
  Dumbbell,
  Play,
  ShieldAlert,
  Sparkles,
  ClipboardList,
  FileSignature,
  CalendarClock,
} from "lucide-react";

const APPLIES = [
  { icon: Wifi, label: "İnternet aboneliği" },
  { icon: Tv, label: "TV / Kablo / IPTV" },
  { icon: Smartphone, label: "Mobil hat" },
  { icon: Play, label: "Dijital platformlar" },
  { icon: Dumbbell, label: "Spor salonu" },
  { icon: FileSignature, label: "Online üyelikler" },
];

const PRECHECK = [
  "Sözleşme veya taahhüt süreniz doldu mu?",
  "Son fatura elinizde var mı?",
  "Cayma bedeli veya taahhüt farkı çıkarılmış mı?",
  "Modem, dekoder veya cihaz iadesi gerekiyor mu?",
];

const HOW_FESHET = [
  { icon: ClipboardList, title: "Sorunu seçer", text: "Kart üzerinden sorununuzu belirlersiniz." },
  { icon: Sparkles, title: "Kurumu belirler", text: "İlgili operatör veya platformu tanımlar." },
  { icon: FileSignature, title: "Metni hazırlar", text: "Kopyalayabileceğiniz fesih dilekçesini oluşturur." },
  { icon: CalendarClock, title: "Takip planı verir", text: "Kontrol tarihleri ve sonraki adımları çizer." },
];

export default function AbonelikIptaliRehberi() {
  const navigate = useNavigate();

  useEffect(() => {
    // Legacy title/desc still set via effect for any older consumers.
  }, []);

  const start = () => navigate("/dosya-baslat?issue=iptal");

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Abonelik İptali Nasıl Yapılır? | Abonelik İptal",
      description:
        "İnternet, TV, mobil hat ve dijital platform abonelikleriniz için iptal sürecini adım adım hazırlayın. Abonelik İptal ile iptal metni ve takip planı oluşturun.",
      url: `${CANONICAL_ORIGIN}/abonelik-iptali-rehberi`,
      inLanguage: "tr-TR",
      isPartOf: { "@type": "WebSite", name: "Abonelik İptal", url: CANONICAL_ORIGIN },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: `${CANONICAL_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Abonelik İptali Rehberi", item: `${CANONICAL_ORIGIN}/abonelik-iptali-rehberi` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Abonelik iptali için hangi kanaldan başvuru yapılır?",
          acceptedAnswer: { "@type": "Answer", text: "Kurumun resmi iptal kanalı (çevrimiçi hesap, mobil uygulama, çağrı merkezi veya bayi) üzerinden başvuru yapılır ve işlem numarası saklanır." },
        },
        {
          "@type": "Question",
          name: "Cayma bedeli çıkarılırsa ne yapmalıyım?",
          acceptedAnswer: { "@type": "Answer", text: "Yazılı itirazınızı kuruma iletin, kayıt numarasını saklayın. Sonuç alınamazsa Tüketici Hakem Heyeti başvurusu için dosyanızı Abonelik İptal ile hazırlayabilirsiniz." },
        },
        {
          "@type": "Question",
          name: "Modem veya dekoder iadesi nasıl olur?",
          acceptedAnswer: { "@type": "Answer", text: "Kurumun bildirdiği iade adresine veya anlaşmalı kargoya teslim edin; kargo fişini ve iade tutanağını mutlaka saklayın." },
        },
      ],
    },
  ];

  return (
    <div>
      <SEO
        title="Abonelik İptali Nasıl Yapılır? | Abonelik İptal"
        description="İnternet, TV, mobil hat ve dijital platform abonelikleriniz için iptal sürecini adım adım hazırlayın. Abonelik İptal ile iptal metni ve takip planı oluşturun."
        path="/abonelik-iptali-rehberi"
        jsonLd={jsonLd}
      />
      <Header />

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14A89F] mb-3">
          Abonelik İptali Rehberi
        </div>
        <h1
          data-testid="rehber-h1"
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] tracking-tight max-w-4xl leading-[1.05]"
        >
          Abonelik iptali nasıl yapılır?
        </h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-3xl">
          İnternet, TV, mobil hat ve dijital platform aboneliklerinde iptal
          sürecinizi adım adım hazırlayın. Abonelik İptal, sorununuza uygun dilekçeyi
          ve takip planını sizin için oluşturur.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            data-testid="rehber-cta-top"
            onClick={start}
            className="fs-btn-primary inline-flex items-center gap-2"
          >
            Abonelik İptal ile abonelik iptal metni hazırla <ArrowRight size={18} />
          </button>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Abonelik İptal tarafından hazırlanmıştır. Abonelik İptal resmi kurum değildir ve
          hukuki danışmanlık vermez.
        </p>
      </section>

      {/* What is it? */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-10">
        <div className="fs-card p-6 md:p-8">
          <h2 className="text-2xl font-bold text-[#0F3D4A]">Abonelik iptali nedir?</h2>
          <p className="mt-3 text-slate-700 leading-relaxed">
            Abonelik iptali; internet, mobil hat, TV, dijital platform veya
            benzeri bir hizmete olan üyeliğinizi resmi olarak sonlandırma
            işlemidir. Doğru başvuru kanalı ve yazılı fesih talebi, cayma
            bedeli itirazlarının ve iade taleplerinin temelidir.
          </p>
        </div>
      </section>

      {/* Applies */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-10">
        <h2 className="text-2xl font-bold text-[#0F3D4A]">
          Hangi aboneliklerde kullanılabilir?
        </h2>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
          {APPLIES.map((a, i) => {
            const Icon = a.icon;
            return (
              <div
                key={i}
                data-testid={`apply-${i}`}
                className="fs-card p-4 flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                  <Icon size={18} className="text-[#0F8F87]" strokeWidth={2.25} />
                </div>
                <span className="text-sm font-semibold text-[#0F172A]">{a.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pre-check */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-10">
        <h2 className="text-2xl font-bold text-[#0F3D4A]">
          İptal öncesi kontrol listesi
        </h2>
        <ul className="mt-5 space-y-2.5">
          {PRECHECK.map((p, i) => (
            <li
              key={i}
              data-testid={`precheck-${i}`}
              className="flex items-start gap-3 fs-card p-4"
            >
              <div className="w-6 h-6 rounded-full bg-[#14A89F] text-white text-xs flex items-center justify-center mt-0.5 shrink-0">
                <CheckCircle2 size={14} />
              </div>
              <span className="text-[#0F172A]/90">{p}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* How Abonelik İptal helps */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-10">
        <h2 className="text-2xl font-bold text-[#0F3D4A]">
          Abonelik İptal nasıl yardımcı olur?
        </h2>
        <div className="mt-5 grid md:grid-cols-4 gap-4">
          {HOW_FESHET.map((h, i) => {
            const Icon = h.icon;
            return (
              <div key={i} className="fs-card p-5">
                <div className="w-11 h-11 rounded-2xl bg-[#0F172A] text-white flex items-center justify-center">
                  <Icon size={18} strokeWidth={2.25} />
                </div>
                <div className="mt-4 text-base font-semibold text-[#0F172A]">
                  {h.title}
                </div>
                <div className="mt-1 text-sm text-slate-600 leading-relaxed">
                  {h.text}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA block */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-14">
        <div className="rounded-3xl bg-[#0F172A] text-white p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#22D3CE] mb-2">
              Hazır mısınız?
            </div>
            <div className="text-2xl md:text-3xl font-bold">
              Kart tabanlı adımlarla iptal başvurunuz hazır.
            </div>
          </div>
          <button
            data-testid="rehber-cta-bottom"
            onClick={start}
            className="fs-btn-primary inline-flex items-center gap-2 whitespace-nowrap"
          >
            Abonelik İptal ile abonelik iptal metni hazırla <ArrowRight size={18} />
          </button>
        </div>
        <div className="mt-4 flex items-start gap-2 text-xs text-slate-500">
          <ShieldAlert size={14} className="mt-0.5 shrink-0" />
          <span>
            Abonelik İptal resmi kurum değildir ve hukuki danışmanlık vermez.
            Hazırlanan metinleri göndermeden önce siz kontrol edersiniz.
          </span>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 md:px-8 mt-10 text-sm text-slate-600">
        Diğer başvurular için:{" "}
        <Link to="/iade-itiraz" className="font-semibold text-[#0F3D4A] underline underline-offset-4">
          İade & İtiraz
        </Link>
        {" · "}
        <Link to="/tuketici-hakem-heyeti" className="font-semibold text-[#0F3D4A] underline underline-offset-4">
          Tüketici Hakem Heyeti
        </Link>
      </div>

      <Footer />
    </div>
  );
}
